/**
 * PA performance (Reporting, 2026-10-19): how the Patient Advocate team is
 * doing this period against the one before it. Counts and PA names only; a
 * resident never appears here. Every number is the server's (the backend
 * pa-performance report); this screen only compares, sorts and draws them.
 *
 * Order on the page: controls, the comparison stated in words, three headline
 * cards, a cumulative trend and a per-PA bar chart (one measure at a time),
 * the per-PA table with a docked detail panel, and "How this is counted".
 * Read-only.
 */
import React, { KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Minus, Sparkles } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { DetailDock, DualScroll, useNarrow, useSheetModal } from '../../components/board';
import type { BoardDetailSection } from '../../components/board';
import { HatchPattern, tideAxisLine, tideAxisTick, tideGrid, tideTooltip, useChartMotion } from '../../components/tide';
import {
  PaPerformanceAdvocate, PaPerformanceData, PerfCompare, PerfMeasures, ReportingResult, reportingApi,
} from '../../services/reportingApi';
import {
  Delta, MEASURE_LABEL, PRESETS, PRESET_LABEL, RangePreset, SortDir, SortKey, TrendMeasure, ZERO_MEASURES,
  advocateKey, computeDelta, cumulativeSeries, dayCount, dayWords, matchingPreset, monthName, money, presetRange, rangeProblem,
  rangeWords, sortAdvocates, todayInZone,
} from './paPerformanceMath';
import './paPerformance.css';

type LoadState = 'loading' | 'ready' | 'error' | 'refused';

export const REFUSED_COPY = 'Your account does not have team PA reporting. Your access has not changed.';

/** The server's own sentence for a bad range (REPORT_*), our copy for everything else. */
function failure(result: ReportingResult<unknown>): { state: LoadState; message: string } {
  const refused = result.status === 401 || result.status === 403 || /^(AUTHZ_|DENY_)/.test(result.code || '');
  if (refused) return { state: 'refused', message: REFUSED_COPY };
  if (result.status && result.status < 500 && /^REPORT_/.test(result.code || '') && result.message && result.message !== 'Request failed') {
    return { state: 'error', message: result.message };
  }
  if (result.status === undefined) return { state: 'error', message: 'Could not reach the reporting service. Nothing was loaded.' };
  return { state: 'error', message: 'The reporting service could not load this report.' };
}

const COMPARE_OPTIONS: Array<{ value: PerfCompare; label: string }> = [
  { value: 'prior_month', label: 'Previous month' },
  { value: 'prior_period', label: 'Period just before' },
];

const DEFINITION_TITLE: Record<string, string> = {
  total_leads: 'Total leads',
  workable_leads: 'Workable leads',
  sales: 'Sales',
  net_sales: 'Net sales',
  sale_value: 'Sale value',
  ears: 'Ears',
  comparison: 'Comparison',
  partial_month: 'Partial month',
  reassignment: 'Reassignment',
  time_zone: 'Time zone',
  scope: 'Scope',
};

const titleCase = (key: string) => key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const DELTA_ICON = { up: ArrowUp, down: ArrowDown, flat: Minus, new: Sparkles } as const;
const DELTA_WORD = { up: 'Up', down: 'Down', flat: 'No change', new: 'New' } as const;

/* ── The change badge: an icon and words, never colour alone ────────────── */

function DeltaBadge({ delta }: { delta: Delta }) {
  const Icon = DELTA_ICON[delta.direction];
  return (
    <span className={`pa-delta pa-delta--${delta.direction}`} data-direction={delta.direction}>
      <Icon size={14} aria-hidden="true" focusable="false" />
      <span aria-hidden="true">{delta.text}</span>
      <span className="pa-sr">{delta.sentence}</span>
    </span>
  );
}

/** The same change inside a table cell: a glyph and the number. */
function DeltaCell({ current, prior }: { current: number; prior: number }) {
  const d = computeDelta(current, prior);
  const glyph = d.direction === 'up' ? '▲' : d.direction === 'down' ? '▼' : d.direction === 'new' ? '▲' : '—';
  return (
    <span className={`pa-delta-cell pa-delta-cell--${d.direction}`}>
      <span aria-hidden="true">{glyph} </span>
      {d.direction === 'flat' ? '0' : d.changeText}
      {d.direction === 'new' && <span className="pa-delta-cell__new"> new</span>}
      <span className="pa-sr"> ({DELTA_WORD[d.direction].toLowerCase()})</span>
    </span>
  );
}

function DeltaLegend() {
  return (
    <div className="pa-delta-legend" role="note" aria-label="How to read the change badges">
      <span className="pa-delta-legend__title">Reading the change</span>
      <span className="pa-delta pa-delta--up"><ArrowUp size={14} aria-hidden="true" focusable="false" /> Up from the comparison</span>
      <span className="pa-delta pa-delta--down"><ArrowDown size={14} aria-hidden="true" focusable="false" /> Down from the comparison</span>
      <span className="pa-delta pa-delta--flat"><Minus size={14} aria-hidden="true" focusable="false" /> No change</span>
      <span className="pa-delta pa-delta--new"><Sparkles size={14} aria-hidden="true" focusable="false" /> New: the comparison had none</span>
    </div>
  );
}

/* ── Headline cards ─────────────────────────────────────────────────────── */

function StatCard({ id, label, current, prior, reference, referenceLabel, children }: {
  id: string; label: string; current: number; prior: number; reference: number | null; referenceLabel: string; children?: React.ReactNode;
}) {
  const delta = computeDelta(current, prior);
  return (
    <article className="pa-stat" aria-labelledby={`${id}-label`} data-testid={`stat-${id}`}>
      <h3 id={`${id}-label`} className="pa-stat__label">{label}</h3>
      <p className="pa-stat__value">{current}</p>
      <p className="pa-stat__compare">
        <span>Comparison: <strong>{prior}</strong></span>
        <DeltaBadge delta={delta} />
      </p>
      {reference !== null && <p className="pa-stat__ref">{referenceLabel}: <strong>{reference}</strong></p>}
      {children && <p className="pa-stat__sub">{children}</p>}
    </article>
  );
}

/* ── Charts ─────────────────────────────────────────────────────────────── */

const C_CURRENT = 'var(--pa-current)';
const C_COMPARE = 'var(--pa-compare)';
const C_REF = 'var(--pa-ref)';

type EndLabelProps = { x?: number; y?: number; index?: number; value?: number | string };

/** Draws a value at the last point of a line only: a direct label without a number on every point. */
const endLabel = (lastIndex: number, text: string, dy: number) => ({ x, y, index, value }: EndLabelProps) => {
  if (index !== lastIndex || x === undefined || y === undefined || value === undefined || value === null) return <g />;
  // A surface-coloured halo keeps the label legible where it crosses another series' line.
  return <text x={x + 8} y={y + dy} fontSize={12} fontWeight={600} fill="var(--tide-ink)" stroke="var(--tide-surface)" strokeWidth={4} paintOrder="stroke" strokeLinejoin="round">{text} {value}</text>;
};

function LegendKey({ items }: { items: Array<{ key: string; label: string; color: string; dash?: string; hatch?: boolean }> }) {
  return (
    <ul className="pa-legend" aria-label="Chart key">
      {items.map((i) => (
        <li key={i.key}>
          <svg width="30" height="12" aria-hidden="true" focusable="false">
            {i.hatch
              ? (<><defs><pattern id={`pa-key-${i.key}`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="6" height="6" fill="var(--tide-surface)" /><rect width="3" height="6" fill={i.color} /></pattern></defs>
                <rect x="1" y="1" width="28" height="10" rx="2" fill={`url(#pa-key-${i.key})`} stroke={i.color} strokeWidth="1.5" /></>)
              : <line x1="1" y1="6" x2="29" y2="6" stroke={i.color} strokeWidth="2" strokeDasharray={i.dash} strokeLinecap="round" />}
          </svg>
          <span>{i.label}</span>
        </li>
      ))}
    </ul>
  );
}

function TableToggle({ open, onToggle, controls }: { open: boolean; onToggle: () => void; controls: string }) {
  return (
    <button type="button" className="pa-link-btn" aria-expanded={open} aria-controls={controls} onClick={onToggle}>
      {open ? 'Hide table' : 'Show as table'}
    </button>
  );
}

function ChartsSection({ data }: { data: PaPerformanceData }) {
  const uid = useId();
  const motion = useChartMotion();
  const [measure, setMeasure] = useState<TrendMeasure>('leads');
  const [trendTable, setTrendTable] = useState(false);
  const [barTable, setBarTable] = useState(false);
  const tabRefs = useRef<Partial<Record<TrendMeasure, HTMLButtonElement | null>>>({});
  const measures: TrendMeasure[] = ['leads', 'workable', 'sales'];
  const { current, comparison, reference } = data.periods;
  const refName = reference ? `All of ${monthName(reference.from)}` : '';
  const label = MEASURE_LABEL[measure];

  const series = useMemo(() => cumulativeSeries(data.trend, { current, comparison, reference }, measure), [data.trend, current, comparison, reference, measure]);
  const lastIndex = (key: 'current' | 'comparison' | 'reference') => {
    for (let i = series.length - 1; i >= 0; i -= 1) if (series[i][key] !== undefined) return i;
    return -1;
  };
  const endCur = series[lastIndex('current')]?.current ?? 0;
  const endCmp = series[lastIndex('comparison')]?.comparison ?? 0;
  // The two end labels sit on opposite sides of their lines, the higher one above, so they never print over each other.
  const curAbove = endCur >= endCmp;
  const endRef = reference ? series[lastIndex('reference')]?.reference ?? 0 : null;
  const trendSummary = `Running total of ${label.toLowerCase()} by day of the period: this period reached ${endCur} by day ${current.days}, the comparison period reached ${endCmp} by day ${comparison.days}${endRef !== null && reference ? `, and all of ${monthName(reference.from)} ended at ${endRef} over ${dayCount(reference.days)}` : ''}.`;

  // Per-PA bars: duplicate display names are told apart so two advocates never share a bar row.
  const bars = useMemo(() => {
    const seen: Record<string, number> = {};
    return [...data.advocates]
      .sort((a, b) => b.current[measure] - a.current[measure] || a.advocate.localeCompare(b.advocate))
      .map((a) => {
        seen[a.advocate] = (seen[a.advocate] || 0) + 1;
        return { key: advocateKey(a), name: seen[a.advocate] > 1 ? `${a.advocate} (${seen[a.advocate]})` : a.advocate, current: a.current[measure], comparison: a.comparison[measure] };
      });
  }, [data.advocates, measure]);
  const barSummary = bars.length === 0
    ? 'No PA has activity in either period.'
    : `${label} by Patient Advocate, this period against the comparison. ${bars[0].name} is highest with ${bars[0].current} (comparison ${bars[0].comparison}); ${bars.length} ${bars.length === 1 ? 'advocate' : 'advocates'} shown.`;
  const hatchId = `${uid.replace(/:/g, '')}-cmp-hatch`;

  const onTabKeys = (e: KeyboardEvent<HTMLButtonElement>) => {
    const i = measures.indexOf(measure);
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % measures.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + measures.length) % measures.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = measures.length - 1;
    else return;
    e.preventDefault();
    setMeasure(measures[next]);
    tabRefs.current[measures[next]]?.focus();
  };

  return (
    <section className="pa-card" aria-labelledby={`${uid}-h`}>
      <div className="pa-card__head">
        <div><p className="pa-eyebrow">Trend and team</p><h2 id={`${uid}-h`}>Charts</h2></div>
        <div role="tablist" aria-label="Measure" className="pa-tabs">
          {measures.map((m) => (
            <button
              key={m}
              ref={(el) => { tabRefs.current[m] = el; }}
              type="button"
              role="tab"
              id={`${uid}-tab-${m}`}
              aria-selected={measure === m}
              aria-controls={`${uid}-panel`}
              tabIndex={measure === m ? 0 : -1}
              className="pa-tab"
              onClick={() => setMeasure(m)}
              onKeyDown={onTabKeys}
            >{MEASURE_LABEL[m]}</button>
          ))}
        </div>
      </div>
      <div role="tabpanel" id={`${uid}-panel`} aria-labelledby={`${uid}-tab-${measure}`} className="pa-charts">
        <figure className="pa-chart">
          <h3 className="pa-chart__title">{label}, running total by day</h3>
          <LegendKey items={[
            { key: 'cur', label: 'This period', color: C_CURRENT },
            { key: 'cmp', label: 'Comparison', color: C_COMPARE, dash: '6 4' },
            ...(reference ? [{ key: 'ref', label: refName, color: C_REF, dash: '2 4' }] : []),
          ]} />
          <div className="pa-chart__plot">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={series} margin={{ top: 22, right: 112, left: 0, bottom: 20 }}>
                <CartesianGrid {...tideGrid} vertical={false} />
                <XAxis dataKey="day" type="number" domain={[1, 'dataMax']} allowDecimals={false} tick={tideAxisTick} axisLine={tideAxisLine} tickLine={false}
                  interval="preserveStartEnd" minTickGap={14} label={{ value: 'Day of the period', position: 'insideBottom', offset: -10, fill: 'var(--tide-ink-2)', fontSize: 12 }} />
                <YAxis tick={tideAxisTick} axisLine={false} tickLine={false} allowDecimals={false} width={36} />
                <Tooltip {...tideTooltip} cursor={{ stroke: 'var(--tide-boundary)', strokeDasharray: '2 4' }} labelFormatter={(d: number) => `Day ${d}`} />
                {reference && (
                  <Line type="monotone" dataKey="reference" name={refName} stroke={C_REF} strokeWidth={2} strokeDasharray="2 4" dot={false} activeDot={{ r: 4 }}
                    label={endLabel(lastIndex('reference'), 'Month', 14) as never} {...motion} />
                )}
                <Line type="monotone" dataKey="comparison" name="Comparison" stroke={C_COMPARE} strokeWidth={2} strokeDasharray="6 4" dot={false} activeDot={{ r: 4 }}
                  label={endLabel(lastIndex('comparison'), 'Before', curAbove ? 16 : -8) as never} {...motion} />
                <Line type="monotone" dataKey="current" name="This period" stroke={C_CURRENT} strokeWidth={2} dot={false} activeDot={{ r: 4 }}
                  label={endLabel(lastIndex('current'), 'Now', curAbove ? -8 : 16) as never} {...motion} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <figcaption className="pa-chart__summary">{trendSummary}</figcaption>
          <TableToggle open={trendTable} onToggle={() => setTrendTable((v) => !v)} controls={`${uid}-trend-table`} />
          {trendTable && (
            <div id={`${uid}-trend-table`} className="pa-mini-scroll" role="region" aria-label={`${label} running total by day, as a table`} tabIndex={0}>
              <table className="pa-table pa-table--plain">
                <caption className="pa-sr">{label}, running total by day of the period</caption>
                <thead><tr>
                  <th scope="col">Day</th><th scope="col">This period</th><th scope="col">Comparison</th>{reference && <th scope="col">{refName}</th>}
                </tr></thead>
                <tbody>{series.map((p) => (
                  <tr key={p.day}>
                    <th scope="row">{p.day}</th><td>{p.current ?? '—'}</td><td>{p.comparison ?? '—'}</td>{reference && <td>{p.reference ?? '—'}</td>}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </figure>

        <figure className="pa-chart">
          <h3 className="pa-chart__title">{label} by Patient Advocate</h3>
          <LegendKey items={[
            { key: 'bcur', label: 'This period', color: C_CURRENT },
            { key: 'bcmp', label: 'Comparison (hatched)', color: C_COMPARE, hatch: true },
          ]} />
          {bars.length === 0 ? <p className="pa-empty-inline">No PA has activity in either period.</p> : (
            <div className="pa-chart__plot">
              <ResponsiveContainer width="100%" height={Math.max(180, bars.length * 54 + 30)}>
                <BarChart data={bars} layout="vertical" margin={{ top: 4, right: 36, left: 4, bottom: 4 }} barCategoryGap="22%" barGap={2}>
                  <defs><HatchPattern id={hatchId} color={C_COMPARE} /></defs>
                  <CartesianGrid {...tideGrid} horizontal={false} />
                  <XAxis type="number" tick={tideAxisTick} axisLine={tideAxisLine} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={tideAxisTick} axisLine={false} tickLine={false} width={132} />
                  <Tooltip {...tideTooltip} />
                  <Bar dataKey="current" name="This period" fill={C_CURRENT} stroke="var(--tide-surface)" strokeWidth={2} radius={[0, 4, 4, 0]}
                    label={{ position: 'right', fill: 'var(--tide-ink-2)', fontSize: 11 }} {...motion} />
                  <Bar dataKey="comparison" name="Comparison" fill={`url(#${hatchId})`} stroke={C_COMPARE} strokeWidth={1} radius={[0, 4, 4, 0]}
                    label={{ position: 'right', fill: 'var(--tide-ink-2)', fontSize: 11 }} {...motion} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <figcaption className="pa-chart__summary">{barSummary}</figcaption>
          {bars.length > 0 && <TableToggle open={barTable} onToggle={() => setBarTable((v) => !v)} controls={`${uid}-bar-table`} />}
          {barTable && bars.length > 0 && (
            <div id={`${uid}-bar-table`} className="pa-mini-scroll" role="region" aria-label={`${label} by Patient Advocate, as a table`} tabIndex={0}>
              <table className="pa-table pa-table--plain">
                <caption className="pa-sr">{label} by Patient Advocate, this period and the comparison</caption>
                <thead><tr><th scope="col">Patient Advocate</th><th scope="col">This period</th><th scope="col">Comparison</th></tr></thead>
                <tbody>{bars.map((b) => <tr key={b.key}><th scope="row">{b.name}</th><td>{b.current}</td><td>{b.comparison}</td></tr>)}</tbody>
              </table>
            </div>
          )}
        </figure>
      </div>
    </section>
  );
}

/* ── Per-PA table with a docked detail panel ────────────────────────────── */

function MeasuresList({ m }: { m: PerfMeasures | null }) {
  const x = m || ZERO_MEASURES;
  const row = (k: string, v: React.ReactNode) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>;
  return (
    <div className="pa-measures">
      <dl className="board-detail__facts">
        {row('Leads', x.leads)}
        {row('Workable leads', x.workable)}
        {row('One ear only', x.one_ear_only)}
        {row('No Patient Liability yet', x.liability_missing)}
        {row('Leads dated by Monday (pre-eMerge)', x.leads_source_reported)}
      </dl>
      <dl className="board-detail__facts">
        {row('Sales (gross)', x.sales)}
        {row('Net sales (still standing)', x.sales_net)}
        {row('Reversed', x.sales_reversed)}
        {row('Not standing (reversed or ended unapproved)', x.sales_not_standing)}
        {row('Sale value (gross)', money(x.sale_value))}
        {row('Sale value (net)', money(x.sale_value_net))}
        {row('Sales with no amount recorded', x.amount_pending)}
      </dl>
      <dl className="board-detail__facts">
        {row('Two ears', x.ears_two)}
        {row('One ear', x.ears_one)}
        {row('Ears not known', x.ears_unknown)}
      </dl>
    </div>
  );
}

const GROUPS: Array<{ key: Extract<SortKey, 'leads' | 'workable' | 'sales'>; label: string }> = [
  { key: 'leads', label: 'Leads' }, { key: 'workable', label: 'Workable' }, { key: 'sales', label: 'Sales' },
];
const SINGLES: Array<{ key: Extract<SortKey, 'sales_net' | 'sale_value' | 'sales_reversed'>; label: string }> = [
  { key: 'sales_net', label: 'Net sales' }, { key: 'sale_value', label: 'Sale value' }, { key: 'sales_reversed', label: 'Reversed' },
];

function SortButton({ label, col, sort, dir, onSort }: { label: string; col: SortKey; sort: SortKey; dir: SortDir; onSort: (col: SortKey) => void }) {
  const on = sort === col;
  return (
    <button type="button" className="board-sort" onClick={() => onSort(col)}>
      {label}
      <span className="board-sort__mark" aria-hidden="true">{on ? (dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </button>
  );
}

function PaTable({ data }: { data: PaPerformanceData }) {
  const uid = useId();
  const [sort, setSort] = useState<SortKey>('leads');
  const [dir, setDir] = useState<SortDir>('desc');
  const [openKey, setOpenKey] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const opener = useRef<string | null>(null);
  const splitRef = useRef<HTMLDivElement>(null);
  const sheet = useNarrow();
  const { current, comparison, reference } = data.periods;

  const rows = useMemo(() => sortAdvocates(data.advocates, sort, dir), [data.advocates, sort, dir]);
  const open = openKey ? rows.find((r) => advocateKey(r) === openKey) || null : null;
  const index = open ? rows.indexOf(open) : -1;
  const totalCur = data.totals.current || ZERO_MEASURES;
  const totalCmp = data.totals.comparison || ZERO_MEASURES;

  const onSort = (col: SortKey) => {
    if (col === sort) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(col); setDir(col === 'name' ? 'asc' : 'desc'); }
  };
  const ariaSort = (col: SortKey): 'ascending' | 'descending' | 'none' => (sort === col ? (dir === 'asc' ? 'ascending' : 'descending') : 'none');

  const openRow = (a: PaPerformanceAdvocate) => { opener.current = advocateKey(a); setOpenKey(advocateKey(a)); };
  const close = useCallback(() => {
    const back = opener.current;
    setOpenKey(null);
    window.setTimeout(() => {
      const btn = back ? document.getElementById(`${uid}-row-${back}`) : null;
      if (btn) btn.focus();
    }, 0);
  }, [uid]);

  useEffect(() => { if (openKey) headingRef.current?.focus(); }, [openKey]);
  useSheetModal(Boolean(openKey && sheet), () => (splitRef.current ? splitRef.current.querySelector<HTMLElement>('.board-dock') : null));

  const onKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!openKey) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.altKey && e.key === 'ArrowUp' && index > 0) { e.preventDefault(); openRow(rows[index - 1]); }
    if (e.altKey && e.key === 'ArrowDown' && index >= 0 && index < rows.length - 1) { e.preventDefault(); openRow(rows[index + 1]); }
  };

  const sections: BoardDetailSection[] = open ? [
    { id: 'current', label: `This period · ${rangeWords(current.from, current.to)}`, render: () => <MeasuresList m={open.current} /> },
    { id: 'comparison', label: `Comparison · ${rangeWords(comparison.from, comparison.to)}`, render: () => <MeasuresList m={open.comparison} /> },
    ...(reference ? [{ id: 'reference', label: `All of ${monthName(reference.from)} · ${dayCount(reference.days)}`, render: () => <MeasuresList m={open.reference} /> }] : []),
  ] : [];

  return (
    <section className="pa-card" aria-labelledby={`${uid}-h`}>
      <div className="pa-card__head">
        <div><p className="pa-eyebrow">One row per Patient Advocate</p><h2 id={`${uid}-h`}>By Patient Advocate</h2></div>
        <p className="pa-muted">Choose a name to open that advocate&rsquo;s full measures</p>
      </div>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className="board report-outcomes-board" onKeyDown={onKeys}>
        <div className="report-outcomes-split" ref={splitRef}>
          <div className="report-outcomes-table">
            <DualScroll label="Performance by Patient Advocate">
              <table className="report-table pa-table">
                <caption className="pa-sr">Leads, workable leads and sales by Patient Advocate: this period, the comparison and the change</caption>
                <thead>
                  <tr>
                    <th scope="col" rowSpan={2} aria-sort={ariaSort('name')} className="pa-th-name">
                      <SortButton label="Patient Advocate" col="name" sort={sort} dir={dir} onSort={onSort} />
                    </th>
                    {GROUPS.map((g) => (
                      <th key={g.key} scope="colgroup" colSpan={3} aria-sort={ariaSort(g.key)} className="pa-th-group">
                        <SortButton label={g.label} col={g.key} sort={sort} dir={dir} onSort={onSort} />
                      </th>
                    ))}
                    {SINGLES.map((s) => (
                      <th key={s.key} scope="col" rowSpan={2} aria-sort={ariaSort(s.key)} className="pa-th-num">
                        <SortButton label={s.label} col={s.key} sort={sort} dir={dir} onSort={onSort} />
                      </th>
                    ))}
                  </tr>
                  <tr className="pa-subhead">
                    {GROUPS.map((g) => (
                      <React.Fragment key={g.key}>
                        <th scope="col">Now</th><th scope="col">Before</th><th scope="col">Change</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>{rows.map((a) => {
                  const k = advocateKey(a);
                  const isOpen = openKey === k;
                  return (
                    <tr key={k} data-open={isOpen ? 'true' : undefined} className={a.advocate_user_id === null ? 'pa-row-none' : undefined}>
                      <th scope="row">
                        <button type="button" id={`${uid}-row-${k}`} className="report-day-btn" aria-expanded={isOpen}
                          aria-controls={isOpen ? `${uid}-dock` : undefined} onClick={() => openRow(a)}>
                          {a.advocate_user_id === null ? 'No PA credited' : a.advocate}
                        </button>
                      </th>
                      {GROUPS.map((g) => (
                        <React.Fragment key={g.key}>
                          <td className="pa-num pa-num--now">{a.current[g.key]}</td>
                          <td className="pa-num">{a.comparison[g.key]}</td>
                          <td className="pa-num"><DeltaCell current={a.current[g.key]} prior={a.comparison[g.key]} /></td>
                        </React.Fragment>
                      ))}
                      <td className="pa-num">{a.current.sales_net}</td>
                      <td className="pa-num">{money(a.current.sale_value)}<span className="pa-cell-sub">net {money(a.current.sale_value_net)}</span></td>
                      <td className="pa-num">{a.current.sales_reversed}</td>
                    </tr>
                  );
                })}</tbody>
                <tfoot><tr>
                  <th scope="row">Total (team)</th>
                  {GROUPS.map((g) => (
                    <React.Fragment key={g.key}>
                      <td className="pa-num pa-num--now">{totalCur[g.key]}</td>
                      <td className="pa-num">{totalCmp[g.key]}</td>
                      <td className="pa-num"><DeltaCell current={totalCur[g.key]} prior={totalCmp[g.key]} /></td>
                    </React.Fragment>
                  ))}
                  <td className="pa-num">{totalCur.sales_net}</td>
                  <td className="pa-num">{money(totalCur.sale_value)}<span className="pa-cell-sub">net {money(totalCur.sale_value_net)}</span></td>
                  <td className="pa-num">{totalCur.sales_reversed}</td>
                </tr></tfoot>
              </table>
            </DualScroll>
          </div>
          {open && (
            <DetailDock
              id={`${uid}-dock`}
              label={`${open.advocate_user_id === null ? 'No PA credited' : open.advocate}: full measures`}
              title={<>{open.advocate_user_id === null ? 'No PA credited' : open.advocate}<span className="board-src">{open.current.leads} leads · {open.current.sales} sales this period</span></>}
              index={index + 1}
              count={rows.length}
              onPrev={index > 0 ? () => openRow(rows[index - 1]) : null}
              onNext={index >= 0 && index < rows.length - 1 ? () => openRow(rows[index + 1]) : null}
              onClose={close}
              sheet={sheet}
              sections={sections}
              memoryKey="reports-pa-performance"
              headingRef={headingRef}
              recordKey={openKey || ''}
            />
          )}
        </div>
      </div>
      <p className="pa-foot">
        Total (team) counts each resident once, so its Leads can be lower than the rows above added together: a resident handed to two PAs counts for each PA and once in the team.
        {' '}&ldquo;No PA credited&rdquo; is sales that have no credited Patient Advocate. Now = this period, Before = the comparison period.
      </p>
    </section>
  );
}

/* ── How this is counted ────────────────────────────────────────────────── */

function CountedPanel({ data }: { data: PaPerformanceData }) {
  const uid = useId();
  const defs = Object.entries(data.definitions || {});
  return (
    <details className="pa-card pa-counted">
      <summary><h2 id={`${uid}-h`}>How this is counted</h2></summary>
      <dl className="report-definitions pa-defs">
        {defs.map(([key, text]) => (
          <div key={key}><dt>{DEFINITION_TITLE[key] || titleCase(key)}</dt><dd>{text}</dd></div>
        ))}
      </dl>
      <h3 className="pa-counted__sub">Open rulings (defaults in use)</h3>
      {data.rulings.length === 0 ? <p className="pa-muted pa-pad">No rulings are open.</p> : (
        <ol className="pa-rulings">
          {data.rulings.map((r) => (
            <li key={r.id}><strong><span className="pa-rulings__id">{r.id}</span> {r.question}</strong><span className="pa-rulings__default">Default in use: {r.default}</span></li>
          ))}
        </ol>
      )}
    </details>
  );
}

/* ── The page section ───────────────────────────────────────────────────── */

/** "Oct 19, 2026, 11:42 AM ET": the moment the numbers were read, on the New York clock. */
function generatedWords(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return at;
  return `${new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }).format(d)} ET`;
}

function isAllZero(m: PerfMeasures | null): boolean {
  return !m || (m.leads === 0 && m.workable === 0 && m.sales === 0);
}

export default function PaPerformanceReport() {
  const uid = useId();
  const [today] = useState(() => todayInZone());
  const initial = presetRange('this_month', today);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [compare, setCompare] = useState<PerfCompare>('prior_month');
  const [data, setData] = useState<PaPerformanceData | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [message, setMessage] = useState('');
  const [exportMessage, setExportMessage] = useState('');
  const [exporting, setExporting] = useState(false);
  const requestId = useRef(0);
  const problem = rangeProblem(from, to);
  const preset = matchingPreset(from, to, today);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    if (rangeProblem(from, to)) return; // an in-flight answer for another range is dropped; nothing is asked for a bad one
    setState('loading');
    setMessage('');
    setData(null);
    const result = await reportingApi.paPerformance(from, to, compare);
    if (id !== requestId.current) return;
    if (result.success && result.data) {
      setData(result.data);
      setState('ready');
      return;
    }
    const f = failure(result);
    setState(f.state);
    setMessage(f.message);
  }, [from, to, compare]);

  useEffect(() => { void load(); }, [load]);

  const applyPreset = (p: RangePreset) => {
    const r = presetRange(p, today);
    setFrom(r.from);
    setTo(r.to);
    setExportMessage('');
  };

  const exportCsv = async () => {
    if (problem) return;
    setExporting(true);
    setExportMessage('');
    const result = await reportingApi.exportPaPerformance(from, to, compare);
    setExporting(false);
    setExportMessage(result || '');
  };

  const cur = data?.totals.current || null;
  const cmp = data?.totals.comparison || null;
  const ref = data?.totals.reference || null;
  const periods = data?.periods;
  const refLabel = periods?.reference ? `All of ${monthName(periods.reference.from)}` : '';
  const empty = Boolean(data) && isAllZero(cur) && isAllZero(cmp);
  const noneNow = Boolean(data) && isAllZero(cur);

  return (
    <div className="pa-perf">
      <section className="pa-card pa-controls" aria-labelledby={`${uid}-controls`}>
        <h2 id={`${uid}-controls`}>Date range and comparison</h2>
        <div className="pa-controls__row">
          <label className="report-date-field">From
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setExportMessage(''); }} />
          </label>
          <label className="report-date-field">To
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setExportMessage(''); }} />
          </label>
          <div className="pa-chips" role="group" aria-label="Date range presets">
            {PRESETS.map((p) => (
              <button key={p} type="button" className="pa-chip" aria-pressed={preset === p} onClick={() => applyPreset(p)}>{PRESET_LABEL[p]}</button>
            ))}
          </div>
          <div className="pa-compare" role="group" aria-label="Compare with">
            <span className="pa-compare__label" aria-hidden="true">Compare with</span>
            <span className="pa-seg">
              {COMPARE_OPTIONS.map((o) => (
                <button key={o.value} type="button" aria-pressed={compare === o.value} onClick={() => { setCompare(o.value); setExportMessage(''); }}>{o.label}</button>
              ))}
            </span>
          </div>
          <button type="button" className="report-primary" onClick={() => void exportCsv()} disabled={state !== 'ready' || exporting || Boolean(problem)}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
        {problem && <p className="report-inline-error" role="alert">{problem}</p>}
        {exportMessage && <p className="report-inline-error" role="alert">{exportMessage}</p>}
      </section>

      {state === 'loading' && !problem && (
        <section className="report-state report-state-loading" aria-busy="true"><p role="status">Loading PA performance&hellip;</p></section>
      )}

      {state === 'refused' && (
        <section className="report-state report-state-refused" role="alert">
          <h2>Report access declined</h2>
          <p>{message}</p>
        </section>
      )}

      {state === 'error' && (
        <section className="report-state report-state-error" aria-live="polite">
          <h2>Report unavailable</h2>
          <p>{message}</p>
          <button type="button" className="report-secondary" onClick={() => void load()}>Try again</button>
        </section>
      )}

      {state === 'ready' && data && periods && (
        <>
          <section className="pa-card pa-caption" aria-label="What is being compared">
            <p className="pa-caption__main">
              <strong>{rangeWords(periods.current.from, periods.current.to)}</strong> ({dayCount(periods.current.days)})
              {' '}vs{' '}
              <strong>{rangeWords(periods.comparison.from, periods.comparison.to)}</strong> ({dayCount(periods.comparison.days)})
            </p>
            {periods.reference && (
              <p>Also shown: all of {monthName(periods.reference.from)} ({dayCount(periods.reference.days)}).</p>
            )}
            {data.compare.reason === 'RANGE_LONGER_THAN_A_MONTH' && (
              <p>This range is longer than a month (or would overlap itself one month back), so it is compared with the period just before it, not with the previous month.</p>
            )}
            {data.through < data.to && (
              <p>Counts run through {dayWords(data.through, true)}{data.through === data.today ? ' (today)' : ''}; the days after it have not happened yet.</p>
            )}
            <p className="pa-muted">Days are America/New_York days. Generated {generatedWords(data.generated_at)}.</p>
          </section>

          {empty && <p className="pa-note" role="status">No leads or sales in this period.</p>}
          {!empty && noneNow && <p className="pa-note" role="status">No leads or sales in this period; the comparison below had activity.</p>}

          <section className="pa-stats" aria-label="Headline numbers">
            <StatCard id="leads" label="Total leads" current={(cur || ZERO_MEASURES).leads} prior={(cmp || ZERO_MEASURES).leads}
              reference={ref ? ref.leads : null} referenceLabel={`${refLabel}`}>
              Each resident counted once for the team.
            </StatCard>
            <StatCard id="workable" label="Workable leads" current={(cur || ZERO_MEASURES).workable} prior={(cmp || ZERO_MEASURES).workable}
              reference={ref ? ref.workable : null} referenceLabel={`${refLabel}`}>
              One ear only {(cur || ZERO_MEASURES).one_ear_only} &middot; No liability yet {(cur || ZERO_MEASURES).liability_missing}
            </StatCard>
            <StatCard id="sales" label="Sales" current={(cur || ZERO_MEASURES).sales} prior={(cmp || ZERO_MEASURES).sales}
              reference={ref ? ref.sales : null} referenceLabel={`${refLabel}`}>
              Net (still standing today) {(cur || ZERO_MEASURES).sales_net} &middot; Reversed {(cur || ZERO_MEASURES).sales_reversed} &middot; {money((cur || ZERO_MEASURES).sale_value)} ({money((cur || ZERO_MEASURES).sale_value_net)} net)
              <span className="pa-stat__fine">Net leaves out every sale that no longer stands: reversed, or its UMED or PA case closed without approval or enrollment. Some older Monday-board sales fall out this way without a reversal.</span>
            </StatCard>
          </section>
          <DeltaLegend />

          <ChartsSection data={data} />
          <PaTable data={data} />
          <CountedPanel data={data} />
        </>
      )}
    </div>
  );
}
